import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import Modal from '../../containers/modal.jsx';
import styles from './account-settings-modal.css';

import {closeAccountSettings} from '../../reducers/modals';
import LanguageSelector from '../../containers/language-selector.jsx';
import supabase from '../../lib/supabase-client.js';

const agentUrl = process.env.AIPNT_DEVICE_AGENT_URL;

const AccountSettingsModal = ({onRequestClose}) => {
    const [activeTab, setActiveTab] = useState('profile');
    const [systemDetails, setSystemDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Password state
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordStatus, setPasswordStatus] = useState(null);
    const [passwordLoading, setPasswordLoading] = useState(false);

    useEffect(() => {
        const fetchDetails = async () => {
            setLoadingDetails(true);
            try {
                const response = await fetch(`${agentUrl}/v1/identity`);
                if (!response.ok) throw new Error('Failed to fetch identity');
                const data = await response.json();
                setSystemDetails(data);
            } catch (err) {
                console.error(err);
                // Mock fallback in case agent is down or not responding during testing
                setSystemDetails({
                    deviceId: 'Unknown Device',
                    deviceName: 'Local System',
                    publicKey: 'N/A'
                });
            } finally {
                setLoadingDetails(false);
            }
        };

        if (activeTab === 'profile' && !systemDetails) {
            fetchDetails();
        }
    }, [activeTab, systemDetails]);

    const handlePasswordChange = async e => {
        e.preventDefault();
        setPasswordStatus(null);
        
        if (newPassword !== confirmPassword) {
            setPasswordStatus({type: 'error', message: 'Passwords do not match.'});
            return;
        }

        if (newPassword.length < 6) {
            setPasswordStatus({type: 'error', message: 'Password must be at least 6 characters.'});
            return;
        }

        if (!supabase) {
            setPasswordStatus({type: 'error', message: 'Supabase configuration is missing.'});
            return;
        }

        setPasswordLoading(true);
        try {
            const {error} = await supabase.auth.updateUser({password: newPassword});
            if (error) throw error;
            
            setPasswordStatus({type: 'success', message: 'Password updated successfully!'});
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            // Note: This requires an active session in Supabase.
            // If session expired, user might need to log in again.
            setPasswordStatus({type: 'error', message: error.message || 'Failed to update password.'});
        } finally {
            setPasswordLoading(false);
        }
    };

    return (
        <Modal
            className={styles.modalContent}
            contentLabel="Account Settings"
            onRequestClose={onRequestClose}
            id="accountSettingsModal"
        >
            <div className={styles.header}>
                <h2 className={styles.headerTitle}>Account Settings</h2>
                <button
                    className={styles.closeButton}
                    onClick={onRequestClose}
                >
                    ✕
                </button>
            </div>

            <div className={styles.body}>
                <div className={styles.sidebar}>
                    <div
                        className={`${styles.tab} ${activeTab === 'profile' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        Profile
                    </div>
                    <div
                        className={`${styles.tab} ${activeTab === 'settings' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('settings')}
                    >
                        Settings
                    </div>
                </div>

                <div className={styles.contentArea}>
                    {activeTab === 'profile' && (
                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>User System Details</h3>
                            {loadingDetails ? (
                                <div className={styles.spinner} />
                            ) : systemDetails ? (
                                <>
                                    <div className={styles.infoGroup}>
                                        <div className={styles.infoLabel}>Device Name</div>
                                        <div className={styles.infoValue}>{systemDetails.deviceName}</div>
                                    </div>
                                    <div className={styles.infoGroup}>
                                        <div className={styles.infoLabel}>Device ID</div>
                                        <div className={styles.infoValue}>{systemDetails.deviceId}</div>
                                    </div>
                                    <div className={styles.infoGroup}>
                                        <div className={styles.infoLabel}>Public Key</div>
                                        <div className={styles.infoValue}>{systemDetails.publicKey}</div>
                                    </div>
                                </>
                            ) : (
                                <div style={{color: '#8888A0'}}>System details not available.</div>
                            )}

                            <h3
                                className={styles.sectionTitle}
                                style={{marginTop: '2rem'}}
                            >Licensing</h3>
                            <div className={styles.infoGroup}>
                                <div className={styles.infoLabel}>Activation Key</div>
                                <div className={styles.infoValue}>
                                    {/* Usually we don't show the full key, but if requested, we would fetch it from an API.
                                        For now, we map it to deviceId or show a masked placeholder. */}
                                    {systemDetails ? `${systemDetails.deviceId.substring(0, 8)}...` : 'N/A'}
                                </div>
                            </div>
                            <div className={styles.infoGroup}>
                                <div className={styles.infoLabel}>Licence Ending Date</div>
                                <div className={styles.infoValue}>
                                    {/* Mocked date since there is no standard endpoint for this yet */}
                                    Lifetime License / Active
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <>
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Language Setup</h3>
                                <div className={styles.infoGroup}>
                                    <div className={styles.infoLabel}>Default Language</div>
                                    <div style={{marginTop: '0.5rem'}}>
                                        {/* Reuse the existing language selector container */}
                                        <LanguageSelector />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Change Password</h3>
                                <form onSubmit={handlePasswordChange}>
                                    <div className={styles.inputGroup}>
                                        <label className={styles.infoLabel}>New Password</label>
                                        <input
                                            type="password"
                                            className={styles.input}
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            placeholder="Enter new password"
                                            required
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label className={styles.infoLabel}>Confirm Password</label>
                                        <input
                                            type="password"
                                            className={styles.input}
                                            value={confirmPassword}
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm new password"
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className={styles.button}
                                        disabled={passwordLoading}
                                    >
                                        {passwordLoading ? 'Updating...' : 'Update Password'}
                                    </button>

                                    {passwordStatus && (
                                        <div className={`${styles.message} ${styles[passwordStatus.type]}`}>
                                            {passwordStatus.message}
                                        </div>
                                    )}
                                </form>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

AccountSettingsModal.propTypes = {
    onRequestClose: PropTypes.func.isRequired
};

const mapStateToProps = state => ({});
const mapDispatchToProps = dispatch => ({
    onRequestClose: () => dispatch(closeAccountSettings())
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountSettingsModal);
